// Trip map archives in the browser (staying on Cloudflare's free tier): each .pmtiles is downloaded
// once into Cache Storage and read locally, so panning costs no Worker requests or R2 reads. The
// first visit draws at once by range requests while the download runs (FieldMap), so a 98 MB city
// isn't a blank map until it has all arrived. A later page load costs one HEAD to check the ETag,
// and nothing at all when offline.
const CACHE = 'pf-map-archives-v1';
const checked = new Map<string, Promise<File>>();

async function download(cache: Cache, url: string): Promise<Response> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`map archive ${url}: ${res.status}`);
  await cache.put(url, res.clone());
  return res;
}

async function load(url: string): Promise<File> {
  const cache = await caches.open(CACHE);
  let res = await cache.match(url);
  if (res) {
    try {
      const head = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
      const etag = head.headers.get('ETag');
      if (head.ok && etag && etag !== res.headers.get('ETag')) res = await download(cache, url);
    } catch {
      // Offline: the cached copy is the point.
    }
  } else {
    res = await download(cache, url);
  }
  return new File([await res.blob()], url);
}

/** The archive from the browser cache if it's there already (no network), else null. */
export async function cachedArchive(url: string): Promise<File | null> {
  try {
    const res = await (await caches.open(CACHE)).match(url);
    return res ? new File([await res.blob()], url) : null;
  } catch {
    return null;
  }
}

/** The archive at `url` as a local File (named by its URL), fetched at most once per page load. */
export function localArchive(url: string): Promise<File> {
  let file = checked.get(url);
  if (!file) {
    file = load(url);
    checked.set(url, file);
    file.catch(() => checked.delete(url));
  }
  return file;
}
